import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { QueryKeys } from "@/constants";
import useAuth from "@/context/auth-provider";
import {
  EditAccountContext,
  EditAccountViewState,
} from "@/pages/containers/editAccount-container";
import { useContext, useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import {
  useMUpdateAccountInfo,
  useMUpdateAccountOtp,
} from "@/hooks/use-update-accountInfo";
import { resetPasswordSchema } from "@/lib/form-schema";
import { queryClient } from "@/hooks/use-query-client";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "../error-notification";

const EditAccountOtp = () => {
  const { payloadData, setPayloadData, setViewState, onOpenChange } =
    useContext(EditAccountContext);
  const { authData } = useAuth();
  const { addError } = useNotificationError();
  const otpQuery = useMUpdateAccountOtp();
  const resendOtpQuery = useMUpdateAccountInfo();

  const form = useForm({
    defaultValues: {
      enteredOTP: "",
    },

    onSubmit: async ({ value }) => {
      setPayloadData({ ...payloadData, ...value });
      otpQuery.mutate(
        {
          email: payloadData.email!,
          phone: payloadData.phone!,
          firstname: payloadData.firstname!,
          lastname: payloadData.lastname!,
          groupId: payloadData.groupId!,
          memberId: payloadData.memberId!,
          enteredOTP: value.enteredOTP,
          username: payloadData.username!,
          accessToken: authData?.user.access_token,
          signature: payloadData.signature!,
        },
        {
          onSuccess: (data) => {
            if (data.success) {
              queryClient.invalidateQueries({
                queryKey: [QueryKeys.DEMOGRAPHICS],
              });
              onOpenChange(false);
              addError({
                message: data.message,
                type: ErrorNotificationType.INFO,
              });
            } else {
              form.reset();
              onOpenChange(false);
              setViewState(EditAccountViewState.ENABLE);
              addError({
                message: data.message,
                type: ErrorNotificationType.ERROR,
              });
            }
          },

          onError: (error) => {
            form.reset();
            onOpenChange(false);
            setViewState(EditAccountViewState.ENABLE);
            addError({
              message: error.message,
              type: ErrorNotificationType.ERROR,
            });
          },
        },
      );
    },
  });

  function handleResendOtp() {
    form.reset();
    resendOtpQuery.mutate(
      {
        email: payloadData.email,
        phone: payloadData.phone,
        firstname: payloadData.firstname,
        lastname: payloadData.lastname,
        groupId: payloadData.groupId,
        memberId: payloadData.memberId,
        username: payloadData.username,
        password: payloadData.password,
        accessToken: authData?.user.access_token,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            setPayloadData({ ...payloadData, signature: data.signature });
            addError({
              message: data.message,
              type: ErrorNotificationType.INFO,
            });
          } else {
            form.reset();
            addError({
              message: data.message,
              type: ErrorNotificationType.ERROR,
            });
          }
        },

        onError: (error) => {
          onOpenChange(false);
          setViewState(EditAccountViewState.ENABLE);
          addError({
            message: error.message,
            type: ErrorNotificationType.ERROR,
          });
        },
      },
    );
  }

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, [form]);

  return (
    <form
      className="flex flex-col items-center gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-col gap-10">
        <h4>Please type the verification code</h4>
        <form.Field
          name="enteredOTP"
          validators={{
            onChange: ({ value }) => {
              const result = resetPasswordSchema.shape.otp.safeParse(value);
              return result.success
                ? undefined
                : result.error.errors[0].message;
            },
          }}
        >
          {(field) => (
            <InputOTP
              maxLength={6}
              value={field.state.value}
              onChange={(val) => field.handleChange(val)}
              onBlur={() => field.handleBlur()}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          )}
        </form.Field>
      </div>

      <div className="flex flex-row items-center justify-center gap-3">
        <span className="py-4 text-center text-[10px] text-gray-500">
          Didn&apos;t get the email?
        </span>
        <Button
          type="button"
          className="w-fit bg-transparent p-0 text-[10px] text-black shadow-none hover:underline"
          onClick={handleResendOtp}
        >
          Resend OTP
        </Button>
      </div>

      <Button className="min-w-32" type="submit">
        Verify OTP
      </Button>
    </form>
  );
};

export default EditAccountOtp;
